import { useEffect, useId, useMemo, useRef, useState } from "react";

/**
 * ADR-0009: any dropdown over user-created data must be searchable.
 * Filters locally, or calls `onSearch` for server-side search (paged lists).
 */
export interface Option {
  value: string;
  label: string;
  hint?: string;
}

interface Props {
  options: Option[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  onSearch?: (q: string) => void;
  allowClear?: boolean;
  /** Offer "Create '<query>'" when nothing matches. */
  onCreate?: (label: string) => void;
  label?: string;
}

export function SearchableSelect({ options, value, onChange, placeholder = "Select…", onSearch, allowClear, onCreate, label }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const selected = options.find((o) => o.value === value) ?? null;
  const filtered = useMemo(() => {
    if (onSearch || !q) return options;
    const needle = q.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(needle) || o.hint?.toLowerCase().includes(needle));
  }, [options, q, onSearch]);
  const canCreate = !!onCreate && q.trim() !== "" && !options.some((o) => o.label.toLowerCase() === q.trim().toLowerCase());

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  useEffect(() => {
    if (open) {
      setActive(0);
      inputRef.current?.focus();
    } else setQ("");
  }, [open]);

  const choose = (v: string) => {
    onChange(v);
    setOpen(false);
  };
  const count = filtered.length + (canCreate ? 1 : 0);

  return (
    <div className="select" ref={rootRef}>
      {label && <span className="field-label">{label}</span>}
      <button type="button" className="select-trigger" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className={selected ? "" : "muted"}>{selected?.label ?? placeholder}</span>
        <span aria-hidden>▾</span>
      </button>
      {open && (
        <div className="select-pop">
          <input
            ref={inputRef}
            className="input"
            placeholder="Search…"
            value={q}
            role="combobox"
            aria-controls={listId}
            aria-expanded
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
              onSearch?.(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") setActive((a) => Math.min(a + 1, count - 1));
              else if (e.key === "ArrowUp") setActive((a) => Math.max(a - 1, 0));
              else if (e.key === "Escape") setOpen(false);
              else if (e.key === "Enter") {
                e.preventDefault();
                if (active < filtered.length) choose(filtered[active].value);
                else if (canCreate) {
                  onCreate!(q.trim());
                  setOpen(false);
                }
              }
            }}
          />
          <ul id={listId} role="listbox" className="select-list">
            {allowClear && value && (
              <li role="option" aria-selected={false} className="select-opt muted" onClick={() => { onChange(null); setOpen(false); }}>
                Clear selection
              </li>
            )}
            {filtered.map((o, i) => (
              <li
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                className={`select-opt${i === active ? " active" : ""}${o.value === value ? " selected" : ""}`}
                onPointerEnter={() => setActive(i)}
                onClick={() => choose(o.value)}
              >
                <span>{o.label}</span>
                {o.hint && <span className="muted small">{o.hint}</span>}
              </li>
            ))}
            {canCreate && (
              <li
                role="option"
                aria-selected={false}
                className={`select-opt${active === filtered.length ? " active" : ""}`}
                onClick={() => { onCreate!(q.trim()); setOpen(false); }}
              >
                + Create “{q.trim()}”
              </li>
            )}
            {count === 0 && <li className="select-empty muted">No matches</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
